import { useState, useRef, useEffect } from "react";
import axios from "axios";
import { MessageCircle, X, Send, Loader2, Printer, FileText, Lightbulb, CheckCircle } from "lucide-react";
import { VoiceInput } from "./VoiceInput";
import { ProposalStatusLookup } from "./ProposalStatusLookup";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

/**
 * Floating AI Support Chat Widget — with multi-turn memory,
 * conversation summarization, and proposal generation.
 */
export const SupportChat = () => {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([
    { role: "agent", text: "Hallo! Ik ben de AI-support van Debate Engine. Hoe kan ik je helpen?" }
  ]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(() => localStorage.getItem("support_session_id"));
  const [lastIntent, setLastIntent] = useState(null);
  const [ctaShownForIdea, setCtaShownForIdea] = useState(null); // last user message that triggered CTA
  const [summary, setSummary] = useState(null);
  const [busyAction, setBusyAction] = useState(null); // "summary" | "proposal"
  const messagesEnd = useRef(null);

  // On open transition, restore previous session messages (only once per open cycle)
  const restoredRef = useRef(false);
  useEffect(() => {
    if (!open) { restoredRef.current = false; return; }
    if (restoredRef.current) return;
    const sid = localStorage.getItem("support_session_id");
    if (!sid) return;
    restoredRef.current = true;
    let cancelled = false;
    axios.get(`${API}/support/history/${sid}`)
      .then(res => {
        if (cancelled || !res.data?.messages?.length) return;
        const restored = [];
        res.data.messages.forEach(m => {
          if (m.user_message) restored.push({ role: "user", text: m.user_message });
          if (m.agent_reply) restored.push({ role: "agent", text: m.agent_reply, proposal: m.proposal || undefined });
        });
        if (restored.length) {
          setMessages([
            { role: "agent", text: "Welkom terug! We pakken het gesprek op waar we gebleven waren." },
            ...restored,
          ]);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    messagesEnd.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const AFFIRMATIVE_PATTERNS = /^(ja|jazeker|graag|prima|oké|okay|ok|doe maar|maak maar|akkoord|yes|sure|please do|alstublieft|alsjeblieft|absoluut)\b[.!?, ]*$/i;

  const send = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", text: userMsg }]);
    setLoading(true);

    // AUTO-TRIGGER: if CTA is showing and user says "ja"-style, fire proposal creation in addition to the normal chat reply.
    // We capture the idea BEFORE the await/setState; afterwards we clear ctaShownForIdea so a follow-up "ja" won't duplicate.
    const shouldAutoMakeProposal = ctaShownForIdea && AFFIRMATIVE_PATTERNS.test(userMsg);
    const ideaForAutoProposal = ctaShownForIdea;

    try {
      const res = await axios.post(`${API}/support/chat`, { message: userMsg, session_id: sessionId });
      const newSid = res.data.session_id;
      if (newSid && newSid !== sessionId) {
        setSessionId(newSid);
        localStorage.setItem("support_session_id", newSid);
      }
      setLastIntent(res.data.intent);
      setMessages(prev => [...prev, { role: "agent", text: res.data.reply }]);

      // Track the latest idea-message so we can build a focused proposal from it
      if (res.data.intent === "idea") {
        setCtaShownForIdea(userMsg);
      }
    } catch {
      setMessages(prev => [...prev, { role: "agent", text: "Er ging iets mis. Probeer het opnieuw of mail info@debate-engine.com." }]);
    }
    setLoading(false);

    if (shouldAutoMakeProposal) {
      // Clear immediately so any subsequent typed "ja" or button click can't double-fire
      setCtaShownForIdea(null);
      // small delay to let the chat reply settle into the UI first
      setTimeout(() => handleMakeProposal(ideaForAutoProposal), 300);
    }
  };

  const handleSummarize = async () => {
    if (!sessionId || busyAction) return;
    setBusyAction("summary");
    try {
      const res = await axios.post(`${API}/support/summarize`, { session_id: sessionId });
      if (res.data.error) {
        setMessages(prev => [...prev, { role: "agent", text: res.data.message || "Samenvatten lukte niet." }]);
      } else {
        setSummary(res.data.summary);
        setMessages(prev => [...prev, { role: "agent", text: "Samenvatting gemaakt — zie hieronder.", summary: res.data.summary }]);
      }
    } catch {
      setMessages(prev => [...prev, { role: "agent", text: "Samenvatten lukte niet." }]);
    }
    setBusyAction(null);
  };

  const handleMakeProposal = async (overrideIdea = null) => {
    if (!sessionId || busyAction) return;
    // Guard: if invoked from a button onClick, the first arg is a MouseEvent — discard it.
    const ideaText = (typeof overrideIdea === "string" && overrideIdea.trim()) ? overrideIdea : (ctaShownForIdea || null);
    setBusyAction("proposal");
    try {
      const body = { session_id: sessionId };
      if (ideaText) body.idea_text = ideaText;
      const res = await axios.post(`${API}/support/proposal`, body);
      if (res.data.error) {
        setMessages(prev => [...prev, { role: "agent", text: res.data.message || "Voorstel maken lukte niet." }]);
      } else {
        setMessages(prev => [...prev, { role: "agent", text: `Voorstel ingediend bij ons AI-managementteam (ref: ${res.data.reference_number}). Bedankt voor je input.`, proposal: res.data.proposal }]);
        setCtaShownForIdea(null); // clear so CTA disappears until a NEW idea is detected
      }
    } catch {
      setMessages(prev => [...prev, { role: "agent", text: "Voorstel maken lukte niet." }]);
    }
    setBusyAction(null);
  };

  const getKey = (msg, idx) => `${msg.role}-${idx}-${(msg.text || "").slice(0, 12)}`;

  const showProposalCTA = !!ctaShownForIdea;

  if (!open) {
    return (
      <button onClick={() => setOpen(true)}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 bg-[#2d8cf0] text-white rounded-full shadow-lg hover:bg-[#1a6fd4] transition-all hover:scale-110 flex items-center justify-center"
        data-testid="support-chat-toggle"
      >
        <MessageCircle className="w-6 h-6" />
      </button>
    );
  }

  return (
    <div className="fixed bottom-6 right-6 z-50 w-80 sm:w-96 bg-white border border-[#E5E5E5] shadow-2xl flex flex-col" style={{ height: "30rem" }} data-testid="support-chat-panel">
      {/* Header */}
      <div className="bg-[#0a0e1a] text-white px-4 py-3 flex items-center justify-between flex-shrink-0">
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
          <span className="text-sm font-bold">Support</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={handleSummarize} disabled={!sessionId || busyAction === "summary"}
            className="text-gray-400 hover:text-white disabled:opacity-40"
            title="Vat dit gesprek samen"
            data-testid="support-chat-summarize"
          >
            {busyAction === "summary" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
          </button>
          <button onClick={() => window.print()}
            className="text-gray-400 hover:text-white"
            title="Print of save as PDF"
            data-testid="support-chat-print"
          >
            <Printer className="w-4 h-4" />
          </button>
          <button onClick={() => setOpen(false)} className="text-gray-400 hover:text-white" data-testid="support-chat-close"><X className="w-4 h-4" /></button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {messages.map((msg, i) => (
          <div key={getKey(msg, i)} className={`flex ${msg.role === "user" ? "justify-end" : "justify-start"}`}>
            <div className={`max-w-[85%] px-3 py-2 text-xs leading-relaxed ${
              msg.role === "user" ? "bg-[#2d8cf0] text-white rounded-t-lg rounded-bl-lg" : "bg-gray-100 text-gray-800 rounded-t-lg rounded-br-lg"
            }`}>
              <div className="whitespace-pre-wrap">{msg.text}</div>
              {msg.summary && <SummaryCard summary={msg.summary} />}
              {msg.proposal && <ProposalCard proposal={msg.proposal} />}
            </div>
          </div>
        ))}
        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 px-3 py-2 rounded-lg"><Loader2 className="w-3 h-3 animate-spin text-gray-400" /></div>
          </div>
        )}
        <div ref={messagesEnd} />
      </div>

      {/* Proposal CTA when an idea was detected */}
      {showProposalCTA && (
        <div className="bg-amber-50 border-t border-amber-200 px-3 py-2 flex items-center justify-between gap-2 flex-shrink-0">
          <div className="flex items-center gap-2 text-[11px] text-amber-900">
            <Lightbulb className="w-3.5 h-3.5 flex-shrink-0" />
            <span>Idee opgemerkt — voorstel maken voor het AI-managementteam?</span>
          </div>
          <button onClick={() => handleMakeProposal()} disabled={busyAction === "proposal"}
            className="px-2 py-1 bg-amber-600 hover:bg-amber-700 text-white text-[10px] font-bold disabled:opacity-50"
            data-testid="support-make-proposal"
          >
            {busyAction === "proposal" ? "Bezig..." : "JA, MAAK"}
          </button>
        </div>
      )}

      {/* Input */}
      <div className="border-t border-[#E5E5E5] p-2 flex gap-1.5 flex-shrink-0">
        <VoiceInput
          onTranscript={(text) => setInput(prev => (prev ? prev + " " : "") + text)}
          size="sm"
          title="Stel je vraag via spraak"
        />
        <input value={input} onChange={e => setInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && send()}
          placeholder="Stel je vraag..."
          className="flex-1 text-xs border border-[#E5E5E5] px-3 py-2 focus:outline-none focus:border-[#2d8cf0]"
          data-testid="support-chat-input"
        />
        <button onClick={send} disabled={loading || !input.trim()}
          className="px-3 py-2 bg-[#2d8cf0] text-white hover:bg-[#1a6fd4] disabled:opacity-40 transition-colors"
          data-testid="support-chat-send"
        >
          <Send className="w-3 h-3" />
        </button>
      </div>
      <ProposalStatusLookup />
    </div>
  );
};

const SummaryCard = ({ summary }) => (
  <div className="mt-2 border-t border-gray-300 pt-2 text-[11px]">
    {summary.topics?.length > 0 && (
      <div className="mb-1"><b>Onderwerpen:</b> {summary.topics.join(", ")}</div>
    )}
    {summary.user_questions?.length > 0 && (
      <div className="mb-1"><b>Vragen:</b>
        <ul className="list-disc ml-4">{summary.user_questions.map(q => <li key={q}>{q}</li>)}</ul>
      </div>
    )}
    {summary.ideas_shared?.length > 0 && (
      <div className="mb-1"><b>Ideeën:</b>
        <ul className="list-disc ml-4">{summary.ideas_shared.map(i => <li key={i}>{i}</li>)}</ul>
      </div>
    )}
    {summary.issues_reported?.length > 0 && (
      <div className="mb-1"><b>Issues:</b>
        <ul className="list-disc ml-4">{summary.issues_reported.map(i => <li key={i}>{i}</li>)}</ul>
      </div>
    )}
    {summary.action_items?.length > 0 && (
      <div className="mb-1"><b>Acties:</b>
        <ul className="list-disc ml-4">{summary.action_items.map(a => <li key={a}>{a}</li>)}</ul>
      </div>
    )}
  </div>
);

const ProposalCard = ({ proposal }) => (
  <div className="mt-2 border-t border-gray-300 pt-2 text-[11px] space-y-1">
    <div className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-green-600" /> <b>{proposal.title}</b></div>
    <div className="text-gray-600">{proposal.summary}</div>
    <div><b>Doel:</b> {proposal.user_goal}</div>
    {proposal.benefits?.length > 0 && (
      <div><b>Voordelen:</b><ul className="list-disc ml-4">{proposal.benefits.map(b => <li key={b}>{b}</li>)}</ul></div>
    )}
    {proposal.considerations?.length > 0 && (
      <div><b>Aandachtspunten:</b><ul className="list-disc ml-4">{proposal.considerations.map(c => <li key={c}>{c}</li>)}</ul></div>
    )}
    <div className="text-gray-500 italic">Ref: {proposal.proposal_id} · {proposal.category} · {proposal.estimated_complexity} complexity</div>
  </div>
);
